import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Filter, Info, Search, X } from 'lucide-react';
import { CourseKpi, CourseKpiCards } from '../../components/training/course/CourseKpiCards';
import { formatCourseDate } from '../../utils/trainingCourses';
import { Layout } from '../../components/Layout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Pagination } from '../../components/ui/Pagination';
import { usePractices } from '../../hooks/usePractices';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { useTeachers } from '../../hooks/useTeachers';
import { useFilters } from '../../hooks/useFilters';
import { useSearch } from '../../hooks/useSearch';
import { usePagination } from '../../hooks/usePagination';
import { trainingBillingStatuses } from '../../data/trainingBillingOptions';
import { formatEuro } from '../../utils/currency';
import { practiceTotal, requesterKindLabel, requesterName } from '../../utils/practices';
import { billingStatusVariant, courseAssociation } from '../../utils/trainingBilling';
export function TrainingBillingPage() {
  const navigate = useNavigate();
  const {
    practices
  } = usePractices();
  const records = useTrainingBilling();
  const courses = useTrainingCourses();
  const teachers = useTeachers();
  const [showFilters, setShowFilters] = useState(false);
  const rows = useMemo(() => records.flatMap(r => {
    const practice = practices.find(p => p.id === r.practiceId);
    if (!practice) return [];
    const assoc = courseAssociation(practice, courses, teachers);
    const startDate = assoc.course?.startDate || practice.courseStartDate;
    return [{
      ...r,
      id: r.practiceId,
      practice,
      assoc,
      practiceRef: practice.id.toUpperCase(),
      startDate,
      requester: requesterName(practice),
      product: assoc.productLabel,
      productCode: assoc.productCode,
      courseCode: assoc.courseCode || '',
      proformaNumber: r.proforma?.number || '',
      invoiceNumber: r.invoice?.number || '',
      amount: practiceTotal(practice)
    }];
  }), [records, practices, courses, teachers]);
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData,
    activeFilterCount
  } = useFilters(rows);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filteredData, ['practiceRef', 'requester', 'product', 'productCode', 'courseCode', 'proformaNumber', 'invoiceNumber']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems
  } = usePagination(searchResult);

  // Summary cards: documents still to issue, issued proformas, invoiced this month
  const kpis = useMemo((): CourseKpi[] => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const proformaToDo = rows.filter(r => r.proforma && !r.proforma.number);
    const proformaDone = rows.filter(r => r.proforma?.number);
    const invoiceToDo = rows.filter(r => r.invoice && !r.invoice.number);
    const invoicedMonth = rows.filter(r => r.invoice?.number && r.invoice.issueDate?.startsWith(monthKey));
    const invoicedTotal = invoicedMonth.reduce((s, r) => s + (r.invoice?.amount ?? r.amount ?? 0), 0);
    const monthName = now.toLocaleDateString('it-IT', {
      month: 'long',
      year: 'numeric'
    });
    const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
    return [{
      label: 'Proforma da fare',
      value: String(proformaToDo.length),
      detail: 'In attesa dell’amministrazione',
      tone: proformaToDo.length > 0 ? 'attention' : 'default'
    }, {
      label: 'Proformato',
      value: String(proformaDone.length),
      detail: 'Proforma emesse'
    }, {
      label: 'Fatture da fare',
      value: String(invoiceToDo.length),
      detail: 'In attesa dell’amministrazione',
      tone: invoiceToDo.length > 0 ? 'attention' : 'default'
    }, {
      label: 'Fatturato nel mese',
      value: formatEuro(invoicedTotal),
      detail: `${monthName} · ${count(invoicedMonth.length, 'fattura', 'fatture')}`
    }];
  }, [rows]);
  const docCell = (doc: {
    progressive: string;
    number?: string;
  } | undefined) => doc ? <div className="whitespace-nowrap" data-id="element-6534" data-mp="gjtzt7yxbm9y">
        <p className="text-sm text-slate-800" data-id="element-6535" data-mp="kjl0c3e95gro">
          {doc.number || <span className="text-slate-400" data-id="element-6536" data-mp="kk46ynygciqe">Da emettere</span>}
        </p>
        <p className="text-xs font-mono text-slate-400" data-id="element-6537" data-mp="252oho8kn4nzw">{doc.progressive}</p>
      </div> : <span className="text-sm text-slate-300" data-id="element-6538" data-mp="20z8qhrrcbful">—</span>;
  return <Layout data-id="element-6539" data-mp="1w0oz3btz9buu">
      <div className="mb-8" data-id="element-6540" data-mp="1w0fa9rko5aqq">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6541" data-mp="1w1ql2bcj7irc">
          Fatturazione Formazione
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-6542" data-mp="1r3yjsqpzykah">
          Posizioni generate dalle pratiche con proforma o fattura da fare.
        </p>
        <p className="flex items-start gap-2 text-sm text-slate-500 mt-3" data-id="element-6543" data-mp="2jqt67t6djtql">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-400" aria-hidden="true" data-id="element-6544" data-mp="2jsw0ipek8914" />
          <span data-id="element-6545" data-mp="2exzxyvbp639r">
            Proforma e fattura sono indipendenti e non obbligatorie. Lo stato è
            impostato dalla pratica e aggiornato dall’amministrazione. Le posizioni
            confluiranno nel modulo Fatturazione.
          </span>
        </p>
      </div>

      <CourseKpiCards items={kpis} data-id="element-6546" data-mp="32ovm9igy2gc9" />

      <Card className="mb-6" noPadding data-id="element-6547" data-mp="32nu998gdbjrw">
        <div className="p-4 flex flex-col gap-4" data-id="element-6548" data-mp="10rv4et1ui3tc">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-6549" data-mp="10u7w3xxensjl">
            <div className="flex-1" data-id="element-6550" data-mp="14snfzsy6wkss">
              <Input placeholder="Cerca richiedente, prodotto, codice corso, n° proforma o fattura..." startContent={<Search className="h-4 w-4" data-id="element-6552" data-mp="w0v91s8djtsy" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-6551" data-mp="14vt352n9dclv" />
            </div>
            <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-6554" data-mp="85itfugpbf5q" />} data-id="element-6553" data-mp="m37dx16uwka9">
              Filtri{' '}
              {activeFilterCount > 0 && <Badge variant="primary" size="sm" className="ml-1" data-id="element-6555" data-mp="22hqiir6cd1tl">
                  {activeFilterCount}
                </Badge>}
            </Button>
          </div>
          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100" data-id="element-6556" data-mp="2hjz179m07pgd">
              <Select label="Stato fatturazione" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...trainingBillingStatuses.map(s => ({
            value: s,
            label: s
          }))]} data-id="element-6557" data-mp="2cmgnz70yeo2k" />
              <div className="col-span-full flex justify-end" data-id="element-6558" data-mp="2vc5dulpy7eb2">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-6560" data-mp="35be4b4lluaso" />} data-id="element-6559" data-mp="2vdzptaj8rw30">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-6561" data-mp="1ax3dwrpd38ks">
        <div className="overflow-x-auto" data-id="element-6562" data-mp="1zh00u4hbjpsf">
          <table className="w-full text-left border-collapse table-auto" data-id="element-6563" data-mp="1zfxco4bds1g7">
            <thead data-id="element-6564" data-mp="24fjnk73ql5lu">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-6565" data-mp="24f1j4n6x7l2u">
                <th className="px-6 py-4" data-id="element-6566" data-mp="297l4w7frhmiq">Pratica</th>
                <th className="px-4 py-4" data-id="element-6567" data-mp="2d8mz29utn8du">Cliente</th>
                <th className="px-4 py-4" data-id="element-6568" data-mp="2dahmpvxh9elm">Corso</th>
                <th className="px-4 py-4 whitespace-nowrap" data-id="element-6569" data-mp="1gj5w42n840fu">Data inizio</th>
                <th className="px-4 py-4" data-id="element-6570" data-mp="1lgxpl13inum5">Proforma</th>
                <th className="px-4 py-4 text-right whitespace-nowrap" data-id="element-6571" data-mp="1lhh407tyk0vf">Importo</th>
                <th className="px-4 py-4" data-id="element-6572" data-mp="1pi0y12j8iodc">Stato</th>
                <th className="px-6 py-4 text-right" data-id="element-6573" data-mp="1uhnfjo63iqry">
                  <span className="sr-only" data-id="element-6574" data-mp="1ujgk8xx5f0xb">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6575" data-mp="31025adppy9ud">
              {paginatedData.length === 0 && <tr data-id="element-6576" data-mp="35yvie513fyu0">
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-slate-500" data-id="element-6577" data-mp="3g0ppwwjuywej">
                    {rows.length === 0 ? 'Nessuna posizione: segna "Proforma da fare" o "Fattura da fare" in una pratica per generarla.' : 'Nessun risultato con la ricerca o i filtri attuali.'}
                  </td>
                </tr>}
              {paginatedData.map(r => <tr key={r.id} onClick={() => navigate(`/training/fatturazione/${r.id}`)} className="hover:bg-slate-50/80 transition-colors cursor-pointer" data-id="element-6578" data-mp="2xbj779j2e4sq">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6579" data-mp="2nclgq122w173">
                    <Link to={`/training/pratiche/${r.practice.id}/modifica`} onClick={e => e.stopPropagation()} className="text-sm font-mono font-medium text-seform-navy hover:text-seform-orange hover:underline" data-id="element-6580" data-mp="2jbrtzb0dgsfs">
                      {r.practiceRef}
                    </Link>
                  </td>
                  <td className="px-4 py-4" data-id="element-6581" data-mp="2tx7vtpniuqrk">
                    <p className="text-sm font-medium text-seform-navy" data-id="element-6582" data-mp="2tyj1xw4fwalu">{r.requester}</p>
                    <p className="text-xs text-slate-400" data-id="element-6583" data-mp="37vg1clymj0ec">
                      {requesterKindLabel(r.practice)}
                    </p>
                  </td>
                  <td className="px-4 py-4" data-id="element-6584" data-mp="2f6sckgeo1sw1">
                    <p className="text-sm text-slate-700" data-id="element-6585" data-mp="2f5h80edech3v">{r.product}</p>
                    <p className="text-xs text-slate-400 font-mono" data-id="element-6586" data-mp="2a9s3dwmisa0p">
                      {r.productCode}
                      {r.courseCode && ` · ${r.courseCode}`}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap" data-id="element-6587" data-mp="2k524mi53gddf">
                    {r.assoc.isOnDemand ? <span className="text-slate-400" data-id="element-6588" data-mp="1rhzcq8uxb0w5">On-demand</span> : formatCourseDate(r.startDate) || '-'}
                  </td>
                  <td className="px-4 py-4" data-id="element-6589" data-mp="bqna9gybway8">{docCell(r.proforma)}</td>
                  <td className="px-4 py-4 text-sm text-right tabular-nums text-slate-700 whitespace-nowrap" data-id="element-6590" data-mp="brygdnf8xusi">
                    {r.amount !== undefined ? formatEuro(r.amount) : '-'}
                  </td>
                  <td className="px-4 py-4" data-id="element-6591" data-mp="1yrdc97zaqvl">
                    <Badge variant={billingStatusVariant(r.status)} size="sm" className="whitespace-nowrap" data-id="element-6592" data-mp="ppwowc6vyx03">
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-6593" data-mp="gq9nvri2jt7k">
                    <Button size="sm" className="whitespace-nowrap" endContent={<ChevronRight className="h-4 w-4" data-id="element-6595" data-mp="31fz345moph0s" />} onClick={e => {
                  e.stopPropagation();
                  navigate(`/training/fatturazione/${r.id}`);
                }} data-id="element-6594" data-mp="grujlkg4di70">
                      Gestisci
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-6596" data-mp="1288vj3p4rxmk">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-6597" data-mp="126xql0jy8jtv" />
        </div>
      </Card>
    </Layout>;
}